import { ReactNode } from '@tanstack/react-router';
import { useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { FormCounterField, FormInputField, lt } from '@mews/framework';
import {
    DropzoneField,
    FormLanguageProvider,
    LocalizedFormInputFields,
    LocalizedFormTextareaFields,
    SelectField,
} from '@mews-commander/form';
import { ContentSection } from '@mews-commander/ui';
import { getSortedLanguages } from '@mews-commander/legacy-entities/src/ducks/languages';
import { useCommanderSelector, useImageSrc } from '@mews-commander/utils';
import { Card, IconButton, Stack, Typography, Image, IconName } from '@optimus-web/core';
import { useProductCategoryOptions } from '../../queries/useProductCategoryOptions';
import { useCurrentAccountingEnvironment } from '../../hooks/useCurrentAccountingEnvironment';
import { ProductFormValues } from '../schemas/productFormSchema';
import { DetailsFormValues } from '../schemas/detailsSchema';

interface Props {
    disabled?: Partial<Record<keyof DetailsFormValues | 'localization', boolean>>;
    helpers: Partial<Record<keyof DetailsFormValues, ReactNode>>;
    enabledUnits?: boolean;
}

export const DetailsStepFields = ({ disabled = {}, helpers, enabledUnits = true }: Props) => {
    const { defaultLanguageCode } = useCurrentEnterprise();

    const languages = useCommanderSelector(getSortedLanguages);

    const productCategoryOptions = useProductCategoryOptions();

    const accountingEnvironment = useCurrentAccountingEnvironment();

    const { trigger, setValue, getValues } = useFormContext<ProductFormValues>();

    const validateFilesSize = async () => {
        await trigger('image');
    };

    const [hideImage, setHideImage] = useState(false);

    const imageId = getValues('imageId');

    const productImgSrc = useImageSrc(imageId);

    const handleImageClear = () => {
        setValue('imageId', null);
        setHideImage(true);
    };

    return (
        <FormLanguageProvider
            defaultLanguageCode={defaultLanguageCode}
            disabled={disabled.localization}
            languages={languages}
        >
            <ContentSection title={lt('DetailPlural')} spacing="300" contentSpacing="200">
                <LocalizedFormInputFields
                    disabled={disabled.names}
                    required={!disabled.names}
                    autoFocus
                    name="names"
                    label={lt('Name')}
                    helper={helpers.names}
                />
                <LocalizedFormInputFields disabled={disabled.shortNames} name="shortNames" label={lt('ShortName')} />
                <LocalizedFormInputFields
                    disabled={disabled.externalNames}
                    label={lt('ExternalName')}
                    name="externalNames"
                />
                <LocalizedFormTextareaFields
                    disabled={disabled.descriptions}
                    helper={helpers.descriptions}
                    label={lt('Description')}
                    name="descriptions"
                />
                {enabledUnits && accountingEnvironment.useBillingUnits ? (
                    <LocalizedFormInputFields
                        disabled={disabled.units}
                        required
                        name="units"
                        label={lt('UnitPlural')}
                    />
                ) : null}
                <SelectField
                    name="productCategoryId"
                    label={lt('Category')}
                    options={productCategoryOptions}
                    emptyValueAsNull
                />
                <FormCounterField
                    disabled={disabled.ordering}
                    label={lt('Ordering')}
                    name="ordering"
                    helper={lt('OrderingHint')}
                />
                {productImgSrc && !hideImage ? (
                    <Stack vertical spacing="50">
                        <Typography textStyle="bodyMediumStrong" as="div">
                            {lt('Image')}
                        </Typography>
                        <Card padding="100" color="black">
                            <Stack itemAlignment="center" distribution="space-between">
                                <Image height={48} width={86} rounded src={productImgSrc} objectFit="cover" />
                                <IconButton
                                    disabled={disabled.image}
                                    variant="ghost"
                                    icon={IconName.Delete}
                                    type="button"
                                    onClick={handleImageClear}
                                />
                            </Stack>
                        </Card>
                    </Stack>
                ) : (
                    <DropzoneField
                        disabled={disabled.image}
                        name="image"
                        label={lt('Image')}
                        accept=".png,.jpg,.jpeg"
                        helper={lt('ProductFormInputHintImage')}
                        onChange={validateFilesSize}
                    />
                )}
            </ContentSection>
            <ContentSection title={lt('Mapping')} spacing="300">
                <FormInputField
                    disabled={disabled.externalIdentifier}
                    label={lt('ExternalIdentifier')}
                    name="externalIdentifier"
                    helper={helpers.externalIdentifier}
                />
            </ContentSection>
        </FormLanguageProvider>
    );
};
